import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useOnboardingStore } from '../store/onboardingStore'
import { Button } from '../components/Button'
import { isMetaVariant } from '../lib/variant'
import {
  MealAnalysisResult,
  type AnalysisResult,
} from '../components/MealAnalysisResult'

type Phase = 'preview' | 'analyzing' | 'results'

/**
 * Canned demo data — matches the app's analysis shape so we render the same
 * shared MealAnalysisResult component. The data feels real (real foods,
 * real numbers) but is deterministic so the demo always tells the same story.
 */
const DEMO_ANALYSIS: AnalysisResult = {
  score: 75,
  overallRisk: 'high',
  foods: [
    {
      name: 'Fried Eggs',
      portion: '2 large eggs',
      sodiumMg: 224,
      potassiumMg: 126,
      phosphorusMg: 172,
      proteinG: 12.6,
      calories: 180, carbsG: 0.8, sugarsG: 0.4, fiberG: 0, fatG: 13.8, saturatedFatG: 4,
      calciumMg: 56, magnesiumMg: 12, ironMg: 1.7, vitaminKMcg: 4, oxalateMg: 0,
      safetyLevel: 'moderate',
    },
    {
      name: 'Hash Browns',
      portion: '1 cup',
      sodiumMg: 500,
      potassiumMg: 400,
      phosphorusMg: 80,
      proteinG: 3,
      calories: 326, carbsG: 34, sugarsG: 0.4, fiberG: 3.1, fatG: 19.5, saturatedFatG: 1.9,
      calciumMg: 14, magnesiumMg: 30, ironMg: 0.8, vitaminKMcg: 9, oxalateMg: 25,
      safetyLevel: 'limit',
      warning: 'High sodium, High potassium',
    },
    {
      name: 'Bacon',
      portion: '3 slices',
      sodiumMg: 525,
      potassiumMg: 135,
      phosphorusMg: 113,
      proteinG: 10.5,
      calories: 130, carbsG: 0.4, sugarsG: 0, fiberG: 0, fatG: 10, saturatedFatG: 3.3,
      calciumMg: 3, magnesiumMg: 9, ironMg: 0.3, vitaminKMcg: 0, oxalateMg: 0,
      safetyLevel: 'limit',
      warning: 'High sodium',
    },
  ],
  totals: {
    sodiumMg: 1249,
    potassiumMg: 661,
    phosphorusMg: 365,
    proteinG: 26.1,
    calories: 636, carbsG: 35.2, sugarsG: 0.8, fiberG: 3.1, fatG: 43.3, saturatedFatG: 9.2,
    calciumMg: 73, magnesiumMg: 51, ironMg: 2.8, vitaminKMcg: 13, oxalateMg: 25,
  },
  swaps: [
    {
      original: 'Hash Browns',
      suggestion: 'Cauliflower rice or leached potatoes',
      impactLabel: '50% less potassium',
      difficulty: 'Worth it',
      reason: 'Cauliflower is naturally low in potassium, and leaching potatoes can reduce their potassium content by up to 50%.',
    },
    {
      original: 'Bacon',
      suggestion: 'Turkey bacon or unsalted lean ham',
      impactLabel: '60% less sodium',
      difficulty: 'Easy swap',
      reason: 'Processed meats like bacon are very high in sodium; turkey bacon offers a lower sodium alternative.',
    },
  ],
  tip: "Bacon brings 42% of this meal's sodium (525 of 1,249 mg). Two slices instead of three saves about 175 mg.",
  reasoning:
    'The combination of hash browns, bacon, and eggs results in a very high sodium intake (1249mg vs. your <600mg per meal target) and elevated potassium. While eggs are a good source of protein, the overall meal exceeds typical per-meal targets for kidney-safe eating.',
}

// Conservative daily limits used for the demo's % DV display. Matches the
// short-flow default (user hasn't entered KidneyStatus yet at this point).
const DEMO_LIMITS = {
  sodium: 2000,
  potassium: 2500,
  phosphorus: 800,
  protein: 50,
}

export function DemoScanStep() {
  const { goNext } = useOnboardingStore()
  const [phase, setPhase] = useState<Phase>('preview')

  useEffect(() => {
    if (phase === 'analyzing') {
      const timer = setTimeout(() => setPhase('results'), 2500)
      return () => clearTimeout(timer)
    }
  }, [phase])

  return (
    <div className="min-h-dvh flex flex-col bg-white">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[600px] mx-auto px-6 py-8">
          <AnimatePresence mode="wait">
            {phase === 'preview' && <PreviewPhase key="preview" onAnalyze={() => setPhase('analyzing')} />}
            {phase === 'analyzing' && <AnalyzingPhase key="analyzing" />}
            {phase === 'results' && (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <MealAnalysisResult
                  result={DEMO_ANALYSIS}
                  limits={DEMO_LIMITS}
                  mealPhotoUrl="/images/demo-breakfast.jpeg"
                  onContinue={() => goNext()}
                  continueLabel="Continue"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

function PreviewPhase({ onAnalyze }: { onAnalyze: () => void }) {
  // On meta variants (cold paid traffic) this screen also serves as the "hook"
  // — it pays off the ad click with the meal photo + a clickable scan
  // affordance. Uses ad-mirror copy ("Stop guessing what you can eat"), bigger
  // visual hierarchy, and a tap-anywhere-on-the-photo target. Organic visitors
  // get the original lighter "Try It Now!" framing.
  //
  // Cold paid traffic also gets no Skip option — the demo IS the conversion
  // moment. Organic keeps the polite escape hatch.
  const isMeta = isMetaVariant()
  const showSkip = !isMeta

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
      {isMeta ? (
        <div className="text-center mb-6">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight">
            Stop guessing what you can eat.
          </h1>
          <p className="text-base text-gray-600 leading-relaxed">
            KidneyPal scans any meal and tells you instantly if it's safe for your kidneys. See it work on a real breakfast in 5 seconds.
          </p>
        </div>
      ) : (
        <div className="text-center mb-6">
          <img src="/images/mascot-jumping.png" alt="" className="w-24 h-24 object-contain mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Try It Now!</h1>
          <p className="text-gray-500">See how KidneyPal analyzes your meals for kidney health</p>
        </div>
      )}

      {/* Meal photo — clickable on meta variants so the "Tap to scan" affordance
          actually works. On organic, kept as a passive image (the green CTA
          below is the primary tap target). */}
      {isMeta ? (
        <motion.button
          type="button"
          onClick={onAnalyze}
          whileTap={{ scale: 0.98 }}
          className="relative rounded-2xl overflow-hidden mb-6 shadow-lg w-full block cursor-pointer"
          aria-label="Analyze this meal"
        >
          <img
            src="/images/demo-breakfast.jpeg"
            alt="Breakfast plate with eggs, hash browns, and bacon"
            className="w-full aspect-[4/3] object-cover pointer-events-none"
          />
          <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur px-3 py-2 rounded-xl shadow-sm flex items-center gap-2 pointer-events-none">
            <span className="text-base">📸</span>
            <span className="text-xs font-semibold text-gray-800">Tap to scan</span>
          </div>
        </motion.button>
      ) : (
        <div className="rounded-2xl overflow-hidden mb-6 shadow-lg">
          <img
            src="/images/demo-breakfast.jpeg"
            alt="Breakfast plate with eggs, hash browns, and bacon"
            className="w-full aspect-[4/3] object-cover"
          />
        </div>
      )}

      {!isMeta && (
        <p className="text-center text-sm text-gray-500 mb-4">
          Tap below to see how our AI analyzes this meal
        </p>
      )}

      <Button onClick={onAnalyze}>
        <span className="flex items-center justify-center gap-2">
          {isMeta ? (
            <>
              See how it works
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
              </svg>
              Analyze This Meal
            </>
          )}
        </span>
      </Button>

      {showSkip && (
        <button
          onClick={() => {
            const store = useOnboardingStore.getState()
            store.goNext()
          }}
          className="w-full mt-3 py-3 text-gray-400 text-sm hover:text-gray-600 transition-colors"
        >
          Skip for now
        </button>
      )}

      {isMeta && (
        <p className="text-xs text-gray-400 text-center mt-4">
          Free demo · No signup needed · 30 seconds
        </p>
      )}
    </motion.div>
  )
}

function AnalyzingPhase() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center py-24"
    >
      <img src="/images/mascot-thinking.png" alt="" className="w-28 h-28 object-contain mb-6" />
      <h2 className="text-xl font-bold text-gray-900 mb-2">Analyzing your meal…</h2>
      <p className="text-gray-500 mb-6">Our AI is checking kidney impact</p>
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2.5 h-2.5 rounded-full bg-primary"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
          />
        ))}
      </div>
    </motion.div>
  )
}
